import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import Tooltip from 'react-tooltip-lite';

import { ReactComponent as Checkmark } from '../../images/checkmark.svg';
import { PrimaryLink } from '../Common/Links';
import SmartLink from '../Common/SmartLink';
import * as styles from './ShortFeatureList.module.css';
import ShortFeatureListMobile from './ShortFeatureListMobile';

const FeatureRow = ({ feature, tiers }) => {
  return (
    <div className={[styles.featureGrid, styles.row].join(' ')} style={{ '--tierCount': tiers.length + 1 }}>
      <div className={styles.featureTitle}>
        {feature.tooltip ? (
          <Tooltip
            direction="right"
            distance={20}
            arrowSize={10}
            arrow={true}
            className={styles.tooltip}
            content={feature.tooltip}
          >
            <p style={{ cursor: 'help' }}>{feature.title}</p>
          </Tooltip>
        ) : (
          <p>{feature.title}</p>
        )}

        {feature.footnote && <p>{feature.footnote}</p>}
      </div>
      {tiers.map((tier, j) => {
        return (
          <div key={j} className={styles.featureMark}>
            {feature.tiers.indexOf(tier) > -1 && <Checkmark />}
          </div>
        );
      })}
    </div>
  );
};

const ShortFeatureListDesktop = ({ featureTiers, list, onScrollCta }) => {
  const topRow = useRef();
  const [hideLede, setHideLede] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState();

  // This logic toggles the lede upon scroll for desktop view widths.
  useEffect(() => {
    setIsLargeScreen(window.innerWidth >= 1024);
    window.addEventListener('resize', () => setIsLargeScreen(window.innerWidth >= 1024));
  }, []);

  useEffect(() => {
    const toggleLede = () => (topRow.current?.offsetTop > 0 ? setHideLede(true) : setHideLede(false));

    if (isLargeScreen) {
      document.addEventListener('scroll', toggleLede);

      return () => {
        document.removeEventListener('scroll', toggleLede);
      };
    } else {
      document.removeEventListener('scroll', toggleLede);
    }
  }, [isLargeScreen]);

  return (
    <div className={styles.container}>
      <div
        ref={topRow}
        className={[styles.featureGrid, styles.topRow].join(' ')}
        style={{ '--tierCount': list?.tiers?.length + 1 }}
      >
        <div />
        {list?.tiers?.map((tier, i) => {
          return (
            <div key={i} className={styles.tierHeader}>
              <div>
                <h4>{tier.title}</h4>
                <p className={[styles.tierLede, hideLede ? styles.hidden : null].join(' ')}>{tier.lede}</p>
              </div>
              <div className={styles.featureButton}>
                <PrimaryLink isButton label={tier.ctaLabel} url={tier.ctaUrl} />
                {tier.secondaryCtaLabel && (
                  <SmartLink className={styles.ctaUrl} to={tier.secondaryCtaUrl}>
                    {tier.secondaryCtaLabel}
                  </SmartLink>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.featureList}>
        <div
          className={styles.featureGrid}
          style={{ '--tierCount': list?.tiers?.length + 1, borderBottom: '1px solid #DEDEDE' }}
        >
          <h4 className={styles.listHeading}>{list?.features?.heading}</h4>
          {list?.tiers?.map((tier, i) => (
            <div key={i} className={styles.tierHeader} />
          ))}
        </div>
        {list.features.list.map((feature, i) => (
          <FeatureRow key={i} tiers={featureTiers} feature={feature} />
        ))}
        <div className={styles.featureGrid} style={{ '--tierCount': list?.tiers?.length + 1 }}>
          <div />
          {list?.tiers?.map((tier, i) => (
            <div key={i} className={styles.tierHeader}>
              {/*eslint-disable-next-line*/}
              <a href="#" className={styles.ctaUrl} onClick={onScrollCta}>
                {tier.footerCtaLabel}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// TODO consolidate desktop and mobile components
const ShortFeatureList = (props) => {
  return (
    <>
      <ShortFeatureListDesktop {...props} />
      <ShortFeatureListMobile {...props} />
    </>
  );
};

ShortFeatureList.propTypes = {
  list: PropTypes.array.isRequired,
};

export default ShortFeatureList;
